import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { Spinner } from '@interstate/components/Spinner';
import { DiscardChangesModal } from '@makemydeal/dr-shared-components';
import CustomerForm from './CustomerForm';
import CustomerCardErrorBoundary from './CustomerCardErrorBoundary';
import { CardWrapper, LoadingOverlay } from './CustomerCard.styles';
import { useCustomerCardActions } from '../hooks/useCustomerCardActions';
import type { CoBuyer, IShopper } from '@makemydeal/dr-platform-types';
import type {
    CustomerCardCapabilities,
    CustomerCardHostState,
    CustomerCardImperativeRef,
    CustomerCardSaveData,
    CustomerFormRef,
    CustomerTabKey
} from '../types';

interface CustomerCardProps {
    customer: CustomerTabKey;
    initialValues?: IShopper | CoBuyer | null;
    dealId?: string;
    onSave?: (data: CustomerCardSaveData) => void;
    onCancel?: () => void;
    onStateChange?: (state: CustomerCardHostState) => void;
    capabilities?: CustomerCardCapabilities;
    /** When true, suppresses the built-in saving overlay; the host receives isSaving via onStateChange to handle loading itself. */
    hideLoader?: boolean;
}

export type CustomerCardRef = CustomerCardImperativeRef;

const CustomerCard = forwardRef<CustomerCardRef, CustomerCardProps>(
    ({ customer, initialValues, dealId, onSave, onCancel, onStateChange, capabilities, hideLoader }, ref) => {
        const formRef = useRef<CustomerFormRef>(null);
        const [isDirty, setIsDirty] = useState(false);
        const [isFormValid, setIsFormValid] = useState(true);
        const [showDiscardModal, setShowDiscardModal] = useState(false);
        const [hasRenderError, setHasRenderError] = useState(false);

        const { apply, cancel, customerCardState } = useCustomerCardActions({
            formRefs: { buyerRef: formRef },
            dealId,
            onSave
        });

        const saveStatus = customerCardState?.saveStatus ?? 'idle';
        const saveError = customerCardState?.saveError;

        useEffect(() => {
            onStateChange?.({
                canApply: isDirty && isFormValid,
                canCancel: true,
                isSaving: saveStatus === 'saving',
                hasRenderError
            });
        }, [isDirty, isFormValid, saveStatus, onStateChange, hasRenderError]);

        const handleFormChange = useCallback((dirty: boolean, valid: boolean) => {
            setIsDirty(dirty);
            setIsFormValid(valid);
        }, []);

        const handleCancelWithDiscardCheck = useCallback(() => {
            if (isDirty) {
                setShowDiscardModal(true);
            } else {
                cancel();
                onCancel?.();
            }
        }, [isDirty, cancel, onCancel]);

        const handleDiscardConfirm = useCallback(() => {
            setShowDiscardModal(false);
            cancel();
            onCancel?.();
        }, [cancel, onCancel]);

        const handleGoBack = useCallback(() => {
            setShowDiscardModal(false);
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                apply,
                cancel: handleCancelWithDiscardCheck
            }),
            [apply, handleCancelWithDiscardCheck]
        );

        return (
            <CardWrapper data-testid={`customer-card-${customer}`}>
                {saveStatus === 'saving' && !hideLoader && (
                    <LoadingOverlay data-testid="customer-card-loading">
                        <Spinner size={60} />
                    </LoadingOverlay>
                )}
                <CustomerCardErrorBoundary onError={() => setHasRenderError(true)}>
                    {saveError && (
                        <Alert type="error" sx={{ marginBottom: '1rem' }} data-testid="customer-card-save-error">
                            <Typography variant="body-sm" tag="span">
                                {saveError}
                            </Typography>
                        </Alert>
                    )}
                    <CustomerForm
                        ref={formRef}
                        customerRole={customer}
                        initialValues={initialValues}
                        capabilities={capabilities}
                        onChange={handleFormChange}
                    />
                    <DiscardChangesModal
                        show={showDiscardModal}
                        onDiscard={handleDiscardConfirm}
                        onGoBack={handleGoBack}
                        hasValidationErrors={false}
                    />
                </CustomerCardErrorBoundary>
            </CardWrapper>
        );
    }
);

CustomerCard.displayName = 'CustomerCard';

export type { CustomerCardProps };
export default CustomerCard;
